import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ScrollRestoration from '@/components/feature/ScrollRestoration';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { siteConfig } from '@/mocks/site';
import { useState, useEffect } from 'react';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const [activeCategory, setActiveCategory] = useState(slug || '');

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory) || categories[0];
  const filteredArticles = articles.filter((a) => a.categorySlug === activeCategory);

  if (!category && slug) {
    return (
      <>
        <SEOHead title="分类不存在 - 91看片网" description="未找到该分类" keywords="91看片网" canonicalUrl={`${siteConfig.url}/category/${slug}`} noindex />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-16 text-center">
            <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-4">分类不存在</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 whitespace-nowrap">返回首页</Link>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const catName = currentCategory.name;
  const catSlug = currentCategory.slug;

  return (
    <>
      <SEOHead
        title={`${catName} - ${siteConfig.name} | 最新${catName}资讯与推荐`}
        description={`${siteConfig.name} ${catName}频道，为您提供最新${catName}相关内容。${currentCategory.description}`}
        keywords={`${catName},影视资讯,影评,${siteConfig.name}`}
        canonicalUrl={`${siteConfig.url}/category/${catSlug}`}
        ogType="website"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${catName} - ${siteConfig.name}`,
          description: currentCategory.description,
          url: `${siteConfig.url}/category/${catSlug}`,
          isPartOf: {
            '@type': 'WebSite',
            name: siteConfig.name,
            url: siteConfig.url,
          },
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <ScrollRestoration />
        <main className="max-w-7xl mx-auto px-4 md:px-6 pt-20">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: catName },
            ]}
          />

          <div className="relative rounded-lg overflow-hidden h-48 md:h-64 mb-8">
            <img
              src={currentCategory.coverImage}
              alt={catName}
              className="w-full h-full object-cover"
              title={`${catName} - 分类横幅`}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-full bg-primary-500 flex items-center justify-center">
                  <i className={`${currentCategory.icon} text-white text-sm`}></i>
                </div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-white">{catName}</h1>
              </div>
              <p className="text-white/70 text-sm">{currentCategory.description}</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-8 pb-6 border-b border-background-200">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 pb-16">
              {filteredArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-foreground-500 mb-4">该分类下暂无文章</p>
              <Link to="/" className="text-primary-500 hover:text-primary-600 whitespace-nowrap">返回首页</Link>
            </div>
          )}
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}